import { useTranslation } from 'react-i18next';
import useBreakpoint from 'hooks/useBreakpoint';
import { format } from 'utils/format';
import styles from './Layout.module.scss';
import { twMerge } from 'tailwind-merge';

export default function SidebarAmountMTP({
  amount,
  dueDate,
  totalInterest,
  interestRate,
  annualPercentageRate,
  drawerOpen = false,
  isError = false,
}: {
  amount?: number;
  dueDate?: string;
  mtp?: number;
  agreementEndDate?: string;
  totalInterest?: number;
  interestRate?: number;
  annualPercentageRate?: number;
  drawerOpen?: boolean;
  isError?: boolean;
}) {
  const { t } = useTranslation();
  const { size } = useBreakpoint();
  const isDesktop = size.index > 2;

  const displayAmountOrEdit = drawerOpen ? (
    <div className="flex items-center" data-test="sidebar-open-drawer-amount">
      <span
        className="flex items-center text-xl font-bold lg:text-xl pr-2 pl-2"
        data-test="amount-value"
      >
        {t('calculator:edit')}
      </span>
    </div>
  ) : (
    <div
      data-test="sidebar-closed-drawer-amount"
      className={twMerge(
        'pr-3 border-r lg:pr-0 lg:border-r-0 lg:pb-3 lg:pl-0 pl-3',
        styles.amountPaddingMobile,
      )}
    >
      <div className="text-xs text-muted" data-test="amount-header">
        {t('common:loan')}
      </div>
      {amount || isError ? (
        <div className="flex gap-1 items-baseline">
          <div className="flex gap-1 items-baseline">
            {isError ? (
              <span
                className="text-xl font-extrabold lg:text-2xl"
                data-test="amount-value"
              >
                -
              </span>
            ) : (
              <>
                <span
                  className="text-xl font-extrabold lg:text-2xl"
                  data-test="amount-value"
                >
                  {format.number(amount, 'amount')}
                </span>
                <span className="text-xs" data-test="amount-currency">
                  {t('common:currency')}
                </span>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="animate-pulse bg-background-accent h-8 w-24" />
      )}
    </div>
  );

  return (
    <div className="flex lg:flex-col w-full">
      {displayAmountOrEdit}

      <div
        data-test="sidebar-dueDate"
        className={twMerge(
          'pr-3 lg:pr-0 lg:border-r-0 lg:pb-3 lg:pl-0 pl-3',
          styles.amountPaddingMobile,
        )}
      >
        <div className="text-xs text-muted" data-test="amount-header">
          {t('common:due_date')}
        </div>

        {dueDate || isError ? (
          <div className="flex gap-1 items-baseline">
            <span className="text-xl font-bold" data-test="amount-value">
              {isError
                ? '-'
                : t('sidebar_amount:due_date_value', {
                    date: format.dateTime(dueDate, 'date'),
                  })}
            </span>
          </div>
        ) : (
          <div className="animate-pulse bg-background-accent h-8 w-24" />
        )}
      </div>

      {isDesktop && (
        <div data-test="number-conditions-container">
          <div data-test="number-conditions-interest">
            <div
              className="pt-1 text-xs text-muted"
              data-test="number-conditions-interest-label"
            >
              {t('common:interestInCzk')}
            </div>
            <div
              className="text-[16px] font-bold"
              data-test="number-conditions-interest-amount"
            >
              {format.number(totalInterest, 'amount')}{' '}
              <span className="text-[12px] font-normal">
                {t('common:currency')}
              </span>
            </div>
          </div>

          <div data-test="number-conditions-interest-rate">
            <div
              className="pt-1 text-xs text-muted"
              data-test="number-conditions-interest-rate-label"
            >
              {t('common:apr')}
            </div>
            <div
              className="text-[16px] font-bold"
              data-test="number-conditions-interest-rate-amount"
            >
              {format.number(interestRate, 'decimal')}
              <span className="text-[12px] font-normal"> %</span>
            </div>
          </div>

          <div data-test="number-conditions-apr">
            <div
              className="pt-1 text-xs text-muted"
              data-test="number-conditions-apr-label"
            >
              {t('common:rspn')}
            </div>
            <div
              className="text-[16px] font-bold"
              data-test="number-conditions-apr-amount"
            >
              {format.number(annualPercentageRate, 'decimal')}
              <span className="text-[12px] font-normal"> %</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
